import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import type { DiaryDayPerson } from './useDiaryDay';
import { useAbility } from '../auth/abilityContext';
import CrewRecordsDialog from '../projects/CrewRecordsDialog';

interface DiaryDayPeopleListProps {
    projectId: string;
    /** Everyone who recorded something for the project on the day. */
    people: DiaryDayPerson[];
    /** Crew keys already named under Personal. */
    staffKeys: Set<string>;
    /**
     * Crew keys already named as Grabungsleiter/Techniker above, mapped to the
     * i18n id of that role. They are listed like everyone else, but carry their
     * role instead of an add button — they are on the day already, just not as
     * Personal.
     */
    leadRoleByKey: Map<string, string>;
    /** How many of `people` are still missing from Personal. */
    missingCount: number;
    onAdd: (name: string) => void;
    onAddAll: () => void;
}

/**
 * Who worked on the project on this Tagebuch day, read off their own entries.
 * Each row carries the person over into Personal; the footer takes everyone
 * still missing at once.
 */
export default function DiaryDayPeopleList({
    projectId,
    people,
    staffKeys,
    leadRoleByKey,
    missingCount,
    onAdd,
    onAddAll
}: DiaryDayPeopleListProps) {
    const intl = useIntl();
    // Who may look into what a person recorded (see `access CrewRecords`).
    const canSeeRecords = useAbility().can('access', 'CrewRecords');
    const [recordsOf, setRecordsOf] = useState<DiaryDayPerson | null>(null);

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2">
                    <FormattedMessage id="diary.dayPeople.title" />
                </Typography>
            </Box>
            <Divider />
            {people.length === 0 ? (
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                    >
                        <FormattedMessage id="diary.dayPeople.empty" />
                    </Typography>
                </Box>
            ) : (
                <Stack divider={<Divider />}>
                    {people.map((person) => {
                        const leadRoleId = leadRoleByKey.get(person.key);
                        const added = staffKeys.has(person.key);
                        return (
                            <Box
                                key={person.key}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    py: 1
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>
                                        {person.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary' }}
                                        noWrap
                                        component="div"
                                    >
                                        {person.sources
                                            .map((source) =>
                                                intl.formatMessage({
                                                    id: `crew.source.${source}`
                                                })
                                            )
                                            .join(', ')}
                                    </Typography>
                                </Box>
                                {leadRoleId ? (
                                    // Named above already — the role says so,
                                    // and there is nothing to take over.
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            flexShrink: 0
                                        }}
                                    >
                                        <FormattedMessage id={leadRoleId} />
                                    </Typography>
                                ) : (
                                    <Tooltip
                                        title={intl.formatMessage({
                                            id: added
                                                ? 'diary.dayPeople.alreadyAdded'
                                                : 'diary.dayPeople.add'
                                        })}
                                    >
                                        {/* A disabled button fires no events, so
                                            the tooltip needs a wrapper. */}
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={added}
                                                aria-label={intl.formatMessage({
                                                    id: 'diary.dayPeople.add'
                                                })}
                                                onClick={() =>
                                                    onAdd(person.name)
                                                }
                                            >
                                                {added ? (
                                                    <CheckIcon fontSize="small" />
                                                ) : (
                                                    <PersonAddIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                {canSeeRecords && (
                                    <Tooltip
                                        title={intl.formatMessage({
                                            id: 'crew.records.action'
                                        })}
                                    >
                                        <IconButton
                                            size="small"
                                            aria-label={intl.formatMessage({
                                                id: 'crew.records.action'
                                            })}
                                            onClick={() => setRecordsOf(person)}
                                        >
                                            <AccountTreeOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        );
                    })}
                    <Box sx={{ px: 1, py: 0.5 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<GroupAddIcon />}
                            disabled={missingCount === 0}
                            onClick={onAddAll}
                        >
                            <FormattedMessage
                                id="diary.dayPeople.addAll"
                                values={{ count: missingCount }}
                            />
                        </Button>
                    </Box>
                </Stack>
            )}
            <CrewRecordsDialog
                projectId={projectId}
                person={recordsOf}
                onClose={() => setRecordsOf(null)}
            />
        </Paper>
    );
}
